import { faChartColumn, faEyeSlash, faShieldAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IDashboardResponse } from '@kcftech/prometheustypescriptapiclient';
import PBIIcon from 'assets/pbi-icon.svg?react';
import { DashboardTabs, getDashboardsToDisplay } from 'components/dashboard/shared';
import { MemoizedTable } from 'components/data_table/Table';
import { TableColumn } from 'components/data_table/types';
import { FavoriteButton } from 'components/favorites/FavoriteButton';
import { SettingsDeleteActionMenu } from 'components/menu/SettingsDeleteActionMenu';
import { SvgIcon } from 'components/svg_icon/SvgIcon';
import { usePermissions } from 'contexts/PermissionsContext';
import { useUser } from 'contexts/UserContext';
import { DashboardsPagedDataSource } from 'custom_hooks/data_hooks/data_source/dashboardsDataSource';
import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { primaryText } from 'styles/theme';
import { KCFColor } from 'styles/themes/KCFColor';

export type DashboardsTableProps = Readonly<{
    className?: string;
    dataSource: DashboardsPagedDataSource;
    tab: DashboardTabs;
    onSettings: (dashboard: IDashboardResponse) => void;
    onDelete: (dashboard: IDashboardResponse) => void;
}>;

const StyledLink = styled(NavLink)`
    text-decoration: none;
    color: ${primaryText};
    font-size: 16px;
`;

const rowKey = ({ Name, Id }: IDashboardResponse) => `Dashboard ${Name} (${Id})`;

export function DashboardsTable({
    className,
    dataSource: { results: dashboards, paging, setPage, isLoading },
    tab,
    onSettings,
    onDelete,
}: DashboardsTableProps) {
    const { favorites } = useUser();

    const displayedDashboards = useMemo(
        () => getDashboardsToDisplay(tab, dashboards, favorites),
        [tab, dashboards, favorites]
    );

    const {
        canViewDashboardActions,
        canEditDashboard,
        canDeleteDashboard,
    } = usePermissions();

    const columns = useMemo<TableColumn<IDashboardResponse>[]>(
        () => [
            {
                header: () => 'Title',
                id: 'name',
                locked: true,
                primary: true,
                value: ({ rowData }) => (
                    <div className="flex-row">
                        {rowData.PowerBiReport !== undefined ? (
                            <SvgIcon
                                className="pr-20"
                                component={PBIIcon}
                                size={18}
                                color={KCFColor.pbiOrange}
                            />
                        ) : (
                            <FontAwesomeIcon
                                className="f-18 px-10"
                                color={KCFColor.taskDashboardPrimary}
                                icon={faChartColumn}
                            />
                        )}
                        <StyledLink to={`dashboards/${rowData.Id}`}>
                            {rowData.Name}
                        </StyledLink>
                    </div>
                ),
            },
            {
                header: () => 'Description',
                id: 'description',
                value: ({ rowData: { Description } }) => (
                    <div className="ellipsis-2-lines f-16">{Description}</div>
                ),
            },
            {
                header: () => 'Favorite',
                id: 'favorite',
                width: '40px',
                value: ({ rowData }) => (
                    <FavoriteButton
                        favoriteItem={rowData}
                        favoriteType="PowerBiDashboard"
                        className="f-16"
                    />
                ),
            },
            {
                header: () => 'Permissions',
                id: 'permissions',
                width: '75px',
                value: ({ rowData: { IsSentryManaged, IsVisibleToCustomers } }) => (
                    <div className="flex-row pl-6">
                        {IsSentryManaged && (
                            <FontAwesomeIcon
                                icon={faShieldAlt}
                                color={KCFColor.tasksd3Primary}
                                className="f-16 mr-12"
                                title="Managed by Sentry"
                                data-cy="dashboard-tile-sentry-icon"
                            />
                        )}
                        {!IsVisibleToCustomers && (
                            <FontAwesomeIcon
                                className="f-16"
                                icon={faEyeSlash}
                                color={KCFColor.primaryText}
                                title="Hidden"
                                data-cy="dashboard-tile-hidden-icon"
                            />
                        )}
                    </div>
                ),
            },
            {
                header: () => 'Actions',
                enabled: canViewDashboardActions,
                id: 'actions',
                width: '30px',
                value: ({ rowData }) => (
                    <SettingsDeleteActionMenu
                        onSettingsClick={() => onSettings(rowData)}
                        onDeleteClick={() => onDelete(rowData)}
                        canEdit={canEditDashboard(rowData)}
                        canDelete={canDeleteDashboard(rowData)}
                    />
                ),
            },
        ],
        [
            canViewDashboardActions,
            canEditDashboard,
            canDeleteDashboard,
            onSettings,
            onDelete,
        ]
    );

    return (
        <MemoizedTable
            tableClassName={className}
            tableStyle="data"
            data={displayedDashboards}
            isLoading={isLoading}
            rowKey={rowKey}
            columns={columns}
            paging={paging}
            onPageChange={setPage}
        />
    );
}
